// sucursales-inventario-multitenant: modo de inventario del tenant
// (`inventoryMode` de `GET /bo/settings/inventory`), con el mismo esquema que
// `displayMode`: un modo activo y los demás disponibles o bloqueados por plan.
// El backend valida lo mismo al cambiar de modo (403 plan, 409 sin tiendas).

export const INVENTORY_MODES = {
    none: "none",
    global: "global",
    merchant: "merchant"
};

export const INVENTORY_MODE_OPTIONS = [
    {
        value: INVENTORY_MODES.none,
        icon: "🚫",
        label: "Sin inventario",
        description:
            "Vendes sin contar unidades: todos tus productos aparecen siempre disponibles.",
        permission: null,
        upgradeFeature: null
    },
    {
        value: INVENTORY_MODES.global,
        icon: "📊",
        label: "Inventario global",
        description:
            "Llevas el stock de cada producto y se descuenta con cada venta.",
        permission: "feature:ActivateInventory",
        upgradeFeature: "inventory"
    },
    {
        value: INVENTORY_MODES.merchant,
        icon: "🏬",
        label: "Inventario por sucursal",
        description:
            "Cada sucursal lleva su stock y el de cada producto es la suma de todas.",
        permission: "feature:ActivateInventoryByMerchant",
        upgradeFeature: "inventoryByMerchant"
    }
];

export const INVENTORY_MODE_STATES = {
    available: "available",
    locked: "locked",
    needsStores: "needsStores"
};

export const findInventoryModeOption = (mode) =>
    INVENTORY_MODE_OPTIONS.find((option) => option.value === mode) || null;

export const inventoryModeLabel = (mode) => {
    const option = findInventoryModeOption(mode);
    return option ? option.label : "";
};

// `hasPermission`: el chequeo de permisos del portal (`$ability.hasPermission`).
export const inventoryModeState = (mode, hasPermission, storesCount = 0) => {
    const option = findInventoryModeOption(mode);
    if (!option || !option.permission) return INVENTORY_MODE_STATES.available;
    if (!hasPermission(option.permission)) {
        return INVENTORY_MODE_STATES.locked;
    }
    if (mode === INVENTORY_MODES.merchant && storesCount === 0) {
        return INVENTORY_MODE_STATES.needsStores;
    }
    return INVENTORY_MODE_STATES.available;
};

// Body de `PATCH /bo/settings/inventory/mode`: `storeId` solo hacia
// `merchant` y `syncFromStores` solo desde `merchant` (el backend rechaza
// cualquiera de los dos fuera de esos casos).
export const buildInventoryModePayload = ({
    currentMode,
    storeId = null,
    syncFromStores = false,
    targetMode
}) => {
    if (targetMode === INVENTORY_MODES.merchant) {
        return storeId
            ? { inventoryMode: targetMode, storeId }
            : { inventoryMode: targetMode };
    }
    if (currentMode === INVENTORY_MODES.merchant) {
        return { inventoryMode: targetMode, syncFromStores };
    }
    return { inventoryMode: targetMode };
};

// Origen del descuento que coincide con cada modo (`stockDecrementedFrom`).
const STOCK_SOURCE_BY_MODE = {
    none: null,
    global: "reference",
    merchant: "merchant"
};

// Órdenes previas al campo, con tienda asignada, se descontaron de la tienda
// (mismo criterio que el backend).
export const resolveStockDecrementedFrom = (order = {}) =>
    order.stockDecrementedFrom || (order.supplyMerchant ? "merchant" : null);

// La orden descontó unidades con otro modo de inventario: si se cancela, el
// backend no las devuelve solas y hay que ajustarlas a mano.
export const isStockReturnManual = (order = {}, inventoryMode) => {
    if (!order.stockDecremented || !inventoryMode) return false;
    const source = resolveStockDecrementedFrom(order);
    return Boolean(source) && source !== STOCK_SOURCE_BY_MODE[inventoryMode];
};

export const stockSourceLabel = (source) =>
    ({
        reference: "el inventario global",
        merchant: "el inventario de la sucursal"
    }[source] || "");
